import { createSlice, createSelector, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@ui/app/store';
import { TAB_CONFIG } from '../constants';

export type TabItem = {
    id: string; // Unique tab identifier
    conversationId: string;
    patientId?: string;
    patientName?: string; // Patient name from OMOP database
    label: string; // Conversation title only
    lastAccessed: number; // timestamp for LRU
    isPreview?: boolean; // (italicized)
};

export interface TabsState {
    tabs: TabItem[];
    activeTabId: string | null;
}

// ===========================
// Configuration
// ===========================
const MAX_TABS = TAB_CONFIG.MAX_TOTAL || 20;

// ===========================
// Utility Functions
// ===========================

/**
 * Generate unique tab ID
 */
function generateTabId(conversationId: string, patientId?: string): string {
    return patientId ? `${conversationId}:${patientId}` : conversationId;
}

/**
 * Find and remove least recently used tabs
 */
function evictLRUTabs(tabs: TabItem[], targetCount: number): TabItem[] {
    if (tabs.length <= targetCount) return tabs;

    // Sort by last accessed (most recent first)
    const sorted = [...tabs].sort((a, b) => b.lastAccessed - a.lastAccessed);

    // Keep only the most recent tabs
    return sorted.slice(0, targetCount);
}

// ===========================
// Initial State (session-based, no persistence)
// ===========================
const initialState: TabsState = {
    tabs: [],
    activeTabId: null,
};

// ===========================
// Redux Slice
// ===========================
const tabsSlice = createSlice({
    name: 'chat/tabs',
    initialState,
    reducers: {
        /**
         * Open a new tab or activate existing one
         * If isPreview is true, this is a VS Code-style preview tab that can be replaced
         */
        openTab(
            state,
            {
                payload,
            }: PayloadAction<{
                conversationId: string;
                patientId?: string;
                patientName?: string;
                label: string;
                isPreview?: boolean;
            }>
        ) {
            const { conversationId, patientId, patientName, label, isPreview = false } = payload;
            const tabId = generateTabId(conversationId, patientId);

            // Check if tab already exists
            const existingIndex = state.tabs.findIndex(t => t.id === tabId);
            if (existingIndex !== -1) {
                // Update last accessed time and activate
                state.tabs[existingIndex].lastAccessed = Date.now();
                state.tabs[existingIndex].label = label; // Update label in case it changed
                if (patientName) {
                    state.tabs[existingIndex].patientName = patientName;
                }
                // If opening as non-preview, pin the existing tab
                if (!isPreview) {
                    state.tabs[existingIndex].isPreview = false;
                }
                state.activeTabId = tabId;
                return;
            }

            // If opening a preview tab and there's already a preview tab, replace it
            if (isPreview) {
                const existingPreviewIndex = state.tabs.findIndex(t => t.isPreview);
                if (existingPreviewIndex !== -1) {
                    // Replace the existing preview tab
                    state.tabs[existingPreviewIndex] = {
                        id: tabId,
                        conversationId,
                        patientId,
                        patientName,
                        label,
                        lastAccessed: Date.now(),
                        isPreview: true,
                    };
                    state.activeTabId = tabId;
                    return;
                }
            }

            // Check if we need to evict LRU tabs
            if (state.tabs.length >= MAX_TABS) {
                // Don't evict the active tab
                const tabsToConsider = state.tabs.filter(t => t.id !== state.activeTabId);
                const tabToEvict = tabsToConsider.sort((a, b) => a.lastAccessed - b.lastAccessed)[0];
                if (tabToEvict) {
                    state.tabs = state.tabs.filter(t => t.id !== tabToEvict.id);
                }
            }

            // Add new tab
            const newTab: TabItem = {
                id: tabId,
                conversationId,
                patientId,
                patientName,
                label,
                lastAccessed: Date.now(),
                isPreview,
            };

            state.tabs.push(newTab);
            state.activeTabId = tabId;
        },

        /**
         * Close a tab
         */
        closeTab(state, { payload }: PayloadAction<{ tabId: string }>) {
            const { tabId } = payload;
            const tabIndex = state.tabs.findIndex(t => t.id === tabId);

            if (tabIndex === -1) return;

            // Remove the tab
            state.tabs.splice(tabIndex, 1);

            // If we closed the active tab, activate the next available tab
            if (state.activeTabId === tabId) {
                if (state.tabs.length > 0) {
                    // Activate the tab that was next to it, or the last tab
                    const newIndex = Math.min(tabIndex, state.tabs.length - 1);
                    state.activeTabId = state.tabs[newIndex].id;
                } else {
                    state.activeTabId = null;
                }
            }
        },

        /**
         * Set active tab
         */
        setActiveTab(state, { payload }: PayloadAction<{ tabId: string }>) {
            const { tabId } = payload;
            const tab = state.tabs.find(t => t.id === tabId);

            if (tab) {
                tab.lastAccessed = Date.now();
                state.activeTabId = tabId;
            }
        },

        /**
         * Update tab label
         */
        updateTabLabel(state, { payload }: PayloadAction<{ tabId: string; label: string }>) {
            const { tabId, label } = payload;
            const tab = state.tabs.find(t => t.id === tabId);

            if (tab) {
                tab.label = label;
            }
        },

        /**
         * Touch tab to update last accessed time
         */
        touchTab(state, { payload }: PayloadAction<{ tabId: string }>) {
            const { tabId } = payload;
            const tab = state.tabs.find(t => t.id === tabId);

            if (tab) {
                tab.lastAccessed = Date.now();
            }
        },

        /**
         * Pin a preview tab (make it permanent, like VS Code double-click)
         */
        pinTab(state, { payload }: PayloadAction<{ tabId: string }>) {
            const { tabId } = payload;
            const tab = state.tabs.find(t => t.id === tabId);

            if (tab && tab.isPreview) {
                tab.isPreview = false;
            }
        },

        /**
         * Clear all tabs
         */
        clearAllTabs(state) {
            state.tabs = [];
            state.activeTabId = null;
        },

        /**
         * Force evict LRU tabs to target count
         */
        forceEvictLRU(state, { payload }: PayloadAction<{ targetCount: number }>) {
            const { targetCount } = payload;
            state.tabs = evictLRUTabs(state.tabs, targetCount);

            // If active tab was evicted, set to most recent
            if (state.activeTabId && !state.tabs.find(t => t.id === state.activeTabId)) {
                state.activeTabId = state.tabs.length > 0 ? state.tabs[0].id : null;
            }
        },

        /**
         * Sync active tab with current URL (called on navigation)
         */
        syncWithUrl(state, { payload }: PayloadAction<{ conversationId: string | null; patientId: string | null }>) {
            const { conversationId, patientId } = payload;

            if (!conversationId) {
                return;
            }

            const tabId = generateTabId(conversationId, patientId || undefined);
            const existingTab = state.tabs.find(t => t.id === tabId);

            if (existingTab) {
                existingTab.lastAccessed = Date.now();
                state.activeTabId = tabId;
            }
        },

        hydrateTabs(state, { payload }: PayloadAction<TabsState>) {
            state.tabs = Array.isArray(payload.tabs) ? payload.tabs : [];
            state.activeTabId = payload.activeTabId ?? null;
        },
    },
});

export const {
    openTab,
    closeTab,
    setActiveTab,
    updateTabLabel,
    touchTab,
    pinTab,
    clearAllTabs,
    forceEvictLRU,
    syncWithUrl,
    hydrateTabs,
} = tabsSlice.actions;

export default tabsSlice.reducer;

// ===========================
// Selectors
// ===========================

/**
 * Select all tabs
 */
export const selectAllTabs = (state: RootState): TabItem[] => state.tabs.tabs;

/**
 * Select active tab ID
 */
export const selectActiveTabId = (state: RootState): string | null => state.tabs.activeTabId;

/**
 * Select active tab
 */
export const selectActiveTab = createSelector(
    [selectAllTabs, selectActiveTabId],
    (tabs, activeTabId) => tabs.find(t => t.id === activeTabId) || null
);

/**
 * Select tab count
 */
export const selectTabCount = createSelector([selectAllTabs], tabs => tabs.length);

/**
 * Check if a tab exists for given conversation/patient
 */
export const makeSelectHasTab = () =>
    createSelector(
        [
            selectAllTabs,
            (_state: RootState, conversationId: string, patientId?: string) => ({ conversationId, patientId }),
        ],
        (tabs, { conversationId, patientId }) => {
            const tabId = generateTabId(conversationId, patientId);
            return tabs.some(t => t.id === tabId);
        }
    );

/**
 * Get tab by conversation and patient ID
 */
export const makeSelectTab = () =>
    createSelector(
        [
            selectAllTabs,
            (_state: RootState, conversationId: string, patientId?: string) => ({ conversationId, patientId }),
        ],
        (tabs, { conversationId, patientId }) => {
            const tabId = generateTabId(conversationId, patientId);
            return tabs.find(t => t.id === tabId) || null;
        }
    );

// ===========================
// Utility exports
// ===========================
export const tabsUtils = {
    generateTabId,
    MAX_TABS,
};

// Legacy exports for backward compatibility (can be removed after migration)
export const openPatientTab = openTab;
export const addHomeTab = () => ({ type: 'NOOP' }); // No-op, home tabs removed
export const touchTabAccess = touchTab;
export const makeSelectTabsForConversation = () => createSelector([selectAllTabs], tabs => tabs); // Return all tabs for compatibility
